import {action, makeAutoObservable, makeObservable, observable} from "mobx"
import Logger from "../Logger";
import utils from "../utils";
import EventEmitter from "../EventEmitter";
import {APIAttachment, APIEmbed} from "discord-api-types/v10";
import {User, UserStore} from "./UserStore";
import {GatewayEvent} from "../clients/shadow/GatewayEvent";
import {ClientEvent, RestShadowMessage, ShadowPayload} from "../clients/shadow/interfaces";
import {ShadowClient} from "../clients/shadow/shadowClient";
import {notifier} from "../Notifier";
import {MessageType} from "../interfaces";

const logger = new Logger("MessageStore")

export class MessageStore extends EventEmitter {
    messages: Message[] = [];

    constructor(
            private client: ShadowClient,
            private userStore: UserStore
    ) {
        super();
        makeObservable(this,
                {
                    messages: observable,
                    setMessages: action,
                }
        )
        this.client = client;
        this.client.on("gateway-event", (event: ShadowPayload) => this.handleGatewayEvent(event));
        this.userStore = userStore;
    }

    setMessages(messages: any[]) {
        this.messages.length = 0;
        logger.trace("Upserting and sorting %s messages", messages.length);
        messages.forEach(message => this.upsertMessage(message, true));
        this.sortByTime();
        logger.trace("Upserted and sorted %s messages", messages.length);
    }

    handleGatewayEvent(event: ShadowPayload) {
        logger.trace("Handling %s", event.type);

        try {
            switch (event.type) {
                case GatewayEvent.MESSAGE_CREATE: {
                    this.upsertMessage(event.data.message || event.data)
                    break;
                }
                case GatewayEvent.MESSAGE_UPDATE: {
                    this.upsertMessage(event.data.message || event.data)
                    break;
                }
                case GatewayEvent.MESSAGE_DELETE: {
                    const restMessage: RestShadowMessage = event.data;
                    const messageIndex = this.messages.findIndex(message => {
                        return message.id === restMessage.id;
                    });

                    if (messageIndex < 0) {
                        throw new Error("No message with id: " + restMessage.id);
                    }

                    const [message] = this.messages.splice(messageIndex, 1);
                    this.emit(ClientEvent.MESSAGE_DELETE, message);
                    break;
                }
                default:
                    break;
            }
        } catch (e) {
            logger.error("Error while handling event of type %s. Error: %s . Data:%o",
                    event.type, e.stack, event.data);
            notifier.error(e.message);
        }

    }

    get(id: string): Message {
        return this.messages.find(message => message.id === id);
    }

    upsertMessage(json: any, quietUpsert?: boolean) {
        if (!quietUpsert) {
            logger.trace("Upserting %o", {timestamp: json.timestamp});
        }

        let message = this.get(json.id);

        if (!message) {
            message = new Message(json, this);
            this.messages.push(message);

            if (!quietUpsert) {
                this.emit(ClientEvent.MESSAGE_CREATE, message);
            }

        } else {
            message.updateFromJson(json);

            if (!quietUpsert) {
                this.emit(ClientEvent.MESSAGE_UPDATE, message);
            }

        }


    }

    sortByTime() {
        return utils.sortArrayObjects(this.messages, 'timestamp')
    }

    // getMessages() {
    //     return this.messages;
    // }
    //
    // getMessagesForRoom(roomId: any) {
    //     return this.messages.filter(message => message.room_id === roomId);
    // }
    //
    // async fetchMessages() {
    //     const joinedRoomId = this.client.getJoinedRoomId();
    //     if (!joinedRoomId) {
    //         throw new Error("Tried to fetch messages before joining a room");
    //     }
    //
    //     const messages = await this.client.getMessagesInRoom(joinedRoomId);
    //     this.setMessages(messages);
    // }
}

export interface Emoji {
    value: string
}

export interface Reaction {
    emoji: Emoji
    userIds: string[]
}

export class Message {
    id: string | null = null;
    timestamp: string;
    author: User | null = null;
    store: any = null;
    room_id: string | null = null;
    avatar: string | null = null;
    embeds: (APIEmbed & { user?: User, userId?: string })[] | null = null;
    attachments: APIAttachment[] | null = null;
    reactions: Reaction[] | null = null;
    avatar_url: string | null = null;
    content: string | null = null;
    type: MessageType;

    constructor(config: string, store: MessageStore) {
        makeAutoObservable(this)
        this.store = store
        this.updateFromJson(config)
    }

    hasJoined(room: any) {
        return this.room_id === room.id;
    }

    updateFromJson(json: any) {
        Object.assign(this, json);

        this.author = this.store.userStore.get(json.author_id) || {};
        const author = this.author;
        if (author) {
            this.avatar_url = utils.createAvatarUrl(author['avatar'], 30)
        }

        const userId = this.embeds?.[0]?.userId
        if (userId) {
            this.embeds[0].user = this.store.userStore.get(userId) || {};
        }
    }

}
